import { DataSampleType } from "../types";
import Highcharts from "highcharts";
import { toMs } from ".";

export const COLOR_CODES: Array<{ p: number; c: string }> = [
	{ p: 0.00, c: '#58D68D' },
	{ p: 0.10, c: '#2ECC71' }, // green
	{ p: 0.20, c: '#F1C40F' }, // yellow
	{ p: 0.30, c: '#F39C12' }, // amber
	{ p: 0.40, c: '#E67E22' }, // orange
	{ p: 0.50, c: '#D35400' }, // dark orange
	{ p: 0.60, c: '#E74C3C' }, // red
	{ p: 0.70, c: '#C0392B' }, // dark red
	{ p: 0.80, c: '#8E44AD' }, // purple
	{ p: 0.90, c: '#7D3C98' }, // dark purple
	{ p: 1.00, c: '#6C3483' }, // deep purple
];

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const hexToRgb = (hex: string) => {
	const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)!;
	return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
};
const rgbToHex = (r: number, g: number, b: number) => {
	const h = (v: number) => v.toString(16).padStart(2, '0');
	return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
};
const lerpColor = (aHex: string, bHex: string, t: number) => {
	const a = hexToRgb(aHex), b = hexToRgb(bHex);
	return rgbToHex(
		Math.round(lerp(a.r, b.r, t)),
		Math.round(lerp(a.g, b.g, t)),
		Math.round(lerp(a.b, b.b, t)),
	);
};

export const MAX_BY_TYPE: Partial<Record<DataSampleType, number>> = {
	[DataSampleType.WIND_SPEED]: 50,//m/s
	[DataSampleType.CURRENT_SPEED]: 6,// m/s
	[DataSampleType.AIRTEMP]: 40 // °C
};

export function normalizeBySampleType(value: number, type: DataSampleType): number {
	const max = MAX_BY_TYPE[type] ?? 50;
	return clamp01(value / max);
}

export function getColorByValueAndType(value: number, type: DataSampleType): string {
	const p = normalizeBySampleType(value, type);

	if (p <= COLOR_CODES[0].p) return COLOR_CODES[0].c;
	if (p >= COLOR_CODES[COLOR_CODES.length - 1].p) return COLOR_CODES[COLOR_CODES.length - 1].c;

	for (let i = 0; i < COLOR_CODES.length - 1; i++) {
		const a = COLOR_CODES[i], b = COLOR_CODES[i + 1];
		if (p >= a.p && p <= b.p) {
			const t = (p - a.p) / (b.p - a.p);
			return lerpColor(a.c, b.c, t);
		}
	}
	return COLOR_CODES[COLOR_CODES.length - 1].c; // fallback
}
export function getFadeValue(timestamp: Date, maxAgeMinutes: number = 30): number{
	const start = toMs(timestamp) / 1000;
	const end = toMs(new Date()) / 1000;
	const diffInSeconds = end - start; // In seconds
	const maxSeconds = maxAgeMinutes * 60;
	const fade = clamp01(diffInSeconds / maxSeconds);
	return fade;
}
export function getColorByValueAndTypeFading(value: number, type: DataSampleType, timestamp?: Date, maxAgeMinutes: number = 30): string {
	const color = getColorByValueAndType(value, type);
	if (timestamp === undefined) {
		return color;
	}
	const fade = getFadeValue(timestamp, maxAgeMinutes);
	const newColor = Highcharts.color(color).tweenTo(Highcharts.color("#FFF"), fade);
	return Highcharts.color(newColor).get("rgba").toString();
}
